import { Component, createRef } from 'react';

import Modal from '../modal/modal.component';

/**
 * Props for the step-up (re-authentication) modal. Mirrors the callback shape
 * of LoginModalComponent: the caller (StepUpService.promptPassword) wires
 * onSubmit/onCancel to a Subject so the entered password flows back out.
 *
 *   onSubmit(password) — user confirmed; password is the plaintext to hash+POST
 *   onCancel()         — user dismissed; the caller treats this as an error
 */
export interface StepUpModalProps {
    onSubmit: (password: string) => void;
    onCancel: () => void;
}

interface StepUpModalState {
    password: string;
    error: string | null;
}

/**
 * Thin mirror of LoginModalComponent. A password field + submit + cancel.
 * On submit it resolves the password back to the caller; on cancel (or the
 * Modal close button) it rejects. Deliberately minimal — the heavy lifting
 * (hashing, /auth/reauth POST, bearer swap) lives in StepUpService.
 */
export class StepUpModalComponent extends Component<StepUpModalProps, StepUpModalState> {
    modalRef = createRef<Modal>();
    // Set once the user submits a password, so the Modal's onClose (which also
    // fires on the [x] close button + on our own unmount) does not ALSO emit a
    // cancel after a successful submit. Single resolution either way.
    private resolved = false;

    static show(props: StepUpModalProps) {
        Modal.createModal(StepUpModalComponent, props);
    }

    constructor(props: StepUpModalProps) {
        super(props);
        this.state = { password: '', error: null };
    }

    private hide() {
        this.modalRef.current?.unmount();
    }

    private submit() {
        if (!this.state.password) {
            this.setState({ error: 'Please enter your password.' });
            return;
        }
        this.resolved = true;
        this.props.onSubmit(this.state.password);
        this.hide();
    }

    private cancel() {
        if (this.resolved) {
            return;
        }
        this.resolved = true;
        this.props.onCancel();
    }

    render() {
        return <Modal ref={this.modalRef} onClose={() => this.cancel()}>
            <div data-modalheader>
                <h2>Confirm your password</h2>
            </div>
            <div data-modalbody>
                <p>For your security, please re-enter your password to continue.</p>
                {/* Form wrapper: silences Chrome's password-outside-form warning and lets
                    the password manager offer autofill. Enter now submits via the form's
                    native behavior — the old onKeyDown handler is gone, it would fire a
                    second submit() alongside onSubmit. */}
                <form method="post" onSubmit={(e) => { e.preventDefault(); this.submit(); }}>
                    <input
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        autoFocus
                        value={this.state.password}
                        placeholder="Password"
                        onChange={(e) => this.setState({ password: e.target.value, error: null })}
                    />
                </form>
                {this.state.error && <p className="error">{this.state.error}</p>}
            </div>
            <div data-modalfooter>
                <button type="button" onClick={() => this.hide()}>Cancel</button>
                <button type="button" onClick={() => this.submit()}>Confirm</button>
            </div>
        </Modal>;
    }
}
